import Big from 'big.js';
import * as bootstrap from 'bootstrap';
import { DollarFormat } from 'smg/util';
import { SaveCalculatorPicks } from '../free-quote/calculator-picks';
import { AwaitModelLoad, PosterCover, ResetModelCamera, type ModelViewerElement } from '../model-swap';
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';

/** The product page's gallery thumbnails, price calculator and gallery sections (type, use and funnel layouts). */
export default function main() {
    bindGallery();
    bindCalculator();
    if (document.querySelector('[data-gallery-group]')) {
        void import('../gallery/sections');
    }
}

/**
 * Every photo is already in the stage; a photo thumbnail just chooses which one shows. A
 * model thumbnail shows the one viewer slide and swaps its model in place, the way the 3D
 * multi-model test page does.
 */
function bindGallery() {
    const $gallery = document.querySelector<HTMLElement>('.product-gallery');
    if (!$gallery) {
        return;
    }

    const $thumbStrip = $gallery.querySelector<HTMLElement>('.product-gallery-thumbs .swiper');
    if ($thumbStrip) {
        new Swiper($thumbStrip, {
            modules: [Navigation],
            slidesPerView: 'auto',
            spaceBetween: 16,
            watchOverflow: true,
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
                addIcons: false,
            },
        });
    }

    const $slides = Array.from($gallery.querySelectorAll<HTMLElement>('[data-gallery-slide]'));
    const $thumbs = Array.from($gallery.querySelectorAll<HTMLButtonElement>('[data-gallery-thumb]'));
    const $modelSlide = $slides.find($slide => $slide.dataset['gallerySlide'] === 'model');
    const $modelViewer = $modelSlide?.querySelector<ModelViewerElement>('model-viewer');
    const models = $modelSlide && $modelViewer ? bindModelSlide($modelSlide, $modelViewer) : null;

    let switching = false;
    const activate = ($thumb: HTMLButtonElement) => {
        const key = $thumb.dataset['galleryThumb'];
        const wasHidden = $slides.some($slide => $slide.dataset['gallerySlide'] === key && $slide.classList.contains('d-none'));
        for (const $slide of $slides) {
            $slide.classList.toggle('d-none', $slide.dataset['gallerySlide'] !== key);
        }
        for (const $other of $thumbs) {
            const active = $other === $thumb;
            $other.classList.toggle('active', active);
            $other.setAttribute('aria-selected', active ? 'true' : 'false');
        }
        return wasHidden;
    };

    for (const $thumb of $thumbs) {
        if ($thumb.dataset['galleryModel']) {
            // Fetch the viewer's code before the click when the pointer says one is coming.
            $thumb.addEventListener('pointerenter', () => void models?.loadViewer(), { once: true });
        }
        $thumb.addEventListener('click', async () => {
            if (switching || $thumb.classList.contains('active')) {
                return;
            }
            const wasHidden = activate($thumb);
            const src = $thumb.dataset['galleryModel'];
            if (!src || !models) {
                return;
            }
            switching = true;
            try {
                await models.show(src, $thumb.dataset['galleryPoster'] ?? '', $thumb.dataset['galleryAlt'] ?? '', wasHidden);
            } finally {
                switching = false;
            }
        });
    }

    // A model on stage at load: its poster stands in until the gallery nears the viewport,
    // then the viewer loads and draws it. Thumbs wait, as they do during a swap.
    const $firstModel = $thumbs.find($thumb => $thumb.classList.contains('active') && $thumb.dataset['galleryModel']);
    if (models && $firstModel) {
        switching = true;
        void models.showFirst($firstModel.dataset['galleryPoster'] ?? '', $gallery).finally(() => {
            switching = false;
        });
    }
}

function nearViewport($el: HTMLElement): Promise<void> {
    return new Promise(resolve => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries.some(entry => entry.isIntersecting)) {
                    observer.disconnect();
                    resolve();
                }
            },
            { rootMargin: '200px' },
        );
        observer.observe($el);
    });
}

/** The viewer slide's model swaps: poster over the stage, new src under it, poster fades once the model is drawn. */
function bindModelSlide($slide: HTMLElement, $modelViewer: ModelViewerElement) {
    const poster = new PosterCover($slide);
    let viewer: Promise<unknown> | null = null;
    const loadViewer = () => viewer ??= import('../shared/model-viewer');

    return {
        loadViewer,
        async showFirst(posterSrc: string, $near: HTMLElement) {
            void poster.show(posterSrc, true);
            await nearViewport($near);
            await loadViewer();
            try {
                if (!$modelViewer.loaded) {
                    await AwaitModelLoad($modelViewer);
                }
            } finally {
                poster.clear();
            }
        },
        async show(src: string, posterSrc: string, alt: string, wasHidden: boolean) {
            if ($modelViewer.getAttribute('src') === src) {
                return;
            }
            // A slide that was hidden until now has nothing to fade from, so its poster is there at once.
            await poster.show(posterSrc, wasHidden);
            await loadViewer();
            const loaded = AwaitModelLoad($modelViewer);
            $modelViewer.setAttribute('src', src);
            $modelViewer.setAttribute('alt', alt);
            ResetModelCamera($modelViewer);
            try {
                await loaded;
            } finally {
                poster.clear();
            }
        },
    };
}

interface sizePrices {
    prices: Record<string, string>;
    moldFee: string;
    freeAtQuantity: number;
}

type calculatorPrices = Record<string, Record<string, sizePrices>>;

/** Mold fee, per-unit price and estimated total for the checked quantity, size and style. */
function bindCalculator() {
    const $calculator = document.querySelector<HTMLFormElement>('[data-product-calculator]');
    if (!$calculator) {
        return;
    }

    const $prices = $calculator.querySelector<HTMLScriptElement>('[data-calculator-prices]');
    const $unit = $calculator.querySelector<HTMLElement>('[data-calculator-unit]');
    const $total = $calculator.querySelector<HTMLElement>('[data-calculator-total]');
    const $mold = $calculator.querySelector<HTMLElement>('[data-calculator-mold]');
    const $moldNote = $calculator.querySelector<HTMLElement>('[data-calculator-mold-note]');
    const $moldFreeAt = $calculator.querySelector<HTMLElement>('[data-calculator-mold-free-at]');
    if (!$prices || !$unit || !$total || !$mold || !$moldNote || !$moldFreeAt) {
        return;
    }

    const prices = JSON.parse($prices.textContent || '{}') as calculatorPrices;

    for (const $hint of $calculator.querySelectorAll<HTMLElement>('[data-bs-toggle="tooltip"]')) {
        new bootstrap.Tooltip($hint);
    }

    // The size reference: the cutout's width is the chosen size over the grid's width.
    const $scale = $calculator.querySelector<HTMLElement>('[data-calculator-scale]');
    const boxInches = Number($scale?.dataset['boxInches'] ?? 0);
    const scaleCutout = (size: string) => {
        if (!$scale || !boxInches) {
            return;
        }
        const inches = parseFloat(size);
        if (!Number.isFinite(inches)) {
            return;
        }
        $scale.style.setProperty('--calculator-cutout-width', `${(inches / boxInches) * 100}%`);
    };

    const $cutouts = $calculator.querySelectorAll<HTMLElement>('[data-calculator-cutout]');
    const showCutout = (type: string) => {
        for (const $cutout of $cutouts) {
            $cutout.hidden = $cutout.dataset['calculatorCutout'] !== type.toLowerCase();
        }
    };

    const checked = (name: string): string => {
        const $input = $calculator.querySelector<HTMLInputElement>(`[name="${name}"]:checked, input[type="hidden"][name="${name}"]`);
        return $input?.value ?? '';
    };

    const update = () => {
        const quantity = checked('calculator-quantity');
        const sizeName = checked('calculator-size');
        scaleCutout(sizeName);
        showCutout(checked('calculator-type'));
        const size = prices[checked('calculator-type').toLowerCase()]?.[sizeName];
        const price = size?.prices[quantity];

        if (!size || !price) {
            $unit.textContent = 'TBD';
            $total.textContent = 'TBD';
            $mold.textContent = 'TBD';
            $moldNote.hidden = true;
            return;
        }

        const unit = Big(price);
        const moldWaived = size.freeAtQuantity > 0 && Number(quantity) >= size.freeAtQuantity;
        const moldFee = moldWaived ? Big(0) : Big(size.moldFee);

        $unit.textContent = DollarFormat(unit);
        $total.textContent = DollarFormat(unit.times(quantity).plus(moldFee));
        $mold.textContent = moldFee.eq(0) ? 'Free' : DollarFormat(moldFee);
        $moldNote.hidden = moldFee.eq(0) || size.freeAtQuantity === 0;
        $moldFreeAt.textContent = size.freeAtQuantity.toLocaleString();
    };

    $calculator.addEventListener('change', update);
    $calculator.addEventListener('submit', e => e.preventDefault());
    $calculator.querySelector('[data-calculator-quote]')?.addEventListener('click', () => {
        const $type = $calculator.querySelector<HTMLInputElement>('[name="calculator-type"]:checked, input[type="hidden"][name="calculator-type"]');
        SaveCalculatorPicks({
            qty: checked('calculator-quantity'),
            size: checked('calculator-size'),
            color: $type?.dataset['quoteColor'],
            type: $type?.dataset['quoteType'],
        });
    });
    update();
}

main();
